import React, { useEffect, useState, useCallback } from 'react';
import { Kid, Quest, Reward, Redemption, User } from './types';
import { api } from './api/client';
import { Header } from './components/Header';
import { TodayCard } from './components/TodayCard';
import { Navigation, NavTab } from './components/Navigation';
import { TodayView } from './components/TodayView';
import { RewardsView } from './components/RewardsView';
import { ManageView } from './components/ManageView';
import { QuestModal } from './components/QuestModal';
import { AddKidModal } from './components/AddKidModal';
import { AdultZoneModal } from './components/AdultZoneModal';
import { RedeemModal } from './components/RedeemModal';
import { LoginModal } from './components/LoginModal';

export const App: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // App data state
  const [kids, setKids] = useState<Kid[]>([]);
  const [selectedKidId, setSelectedKidId] = useState<string>('');
  const [todayDateStr, setTodayDateStr] = useState<string>('');
  const [sharedStars, setSharedStars] = useState<number>(0);
  const [quests, setQuests] = useState<Quest[]>([]);
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [redemptions, setRedemptions] = useState<Redemption[]>([]);

  // Navigation tab
  const [activeTab, setActiveTab] = useState<NavTab>('today');

  // Modals state
  const [isAdultZoneOpen, setIsAdultZoneOpen] = useState(false);
  const [isAddQuestOpen, setIsAddQuestOpen] = useState(false);
  const [isAddKidOpen, setIsAddKidOpen] = useState(false);
  const [selectedRewardToRedeem, setSelectedRewardToRedeem] = useState<Reward | null>(null);

  const fetchAppState = useCallback(async (kidId?: string) => {
    try {
      const data = await api.getAppState(kidId);
      setUser(data.user);
      setKids(data.kids);
      setSelectedKidId(data.selected_kid_id);
      setTodayDateStr(data.today_date_str);
      setSharedStars(data.shared_stars);
      setQuests(data.quests);
      setRewards(data.rewards);
      setRedemptions(data.redemptions);
    } catch (err) {
      console.error('Failed to fetch app state:', err);
    }
  }, []);

  // Initial check
  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        const meRes = await api.getMe();
        if (meRes.loggedIn && meRes.user) {
          setUser(meRes.user);
          await fetchAppState();
        } else {
          // If not logged in, auto-login with demo for frictionless start or show login
          setUser(null);
        }
      } catch (e) {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [fetchAppState]);

  const handleSelectKid = async (kidId: string) => {
    setSelectedKidId(kidId);
    await fetchAppState(kidId);
  };

  const handleToggleQuest = async (questId: string) => {
    // Optimistic toggle
    setQuests((prev) =>
      prev.map((q) =>
        q.id === questId ? { ...q, is_completed_today: !q.is_completed_today } : q
      )
    );

    try {
      const res = await api.toggleQuest(questId);
      // Update kid streaks and stars from response
      setKids((prev) =>
        prev.map((k) =>
          k.id === res.kid_id
            ? {
                ...k,
                current_streak: res.streak,
                personal_stars_available: res.personal_stars,
              }
            : k
        )
      );
      setSharedStars(res.shared_stars);
      // Refresh state to update Working Toward & Rewards progress bars
      await fetchAppState(selectedKidId);
    } catch (err) {
      console.error('Error toggling quest:', err);
      // Revert on failure
      await fetchAppState(selectedKidId);
    }
  };

  const handleCreateQuest = async (data: {
    kid_id: string;
    name: string;
    icon_emoji: string;
    repeat_days: string[];
    time_str?: string;
    points: number;
  }) => {
    await api.createQuest(data);
    await fetchAppState(selectedKidId);
  };

  const handleDeleteQuest = async (questId: string) => {
    await api.deleteQuest(questId);
    await fetchAppState(selectedKidId);
  };

  const handleCreateKid = async (data: {
    name: string;
    avatar_emoji: string;
    color: string;
  }) => {
    const res = await api.createKid(data);
    await fetchAppState(res.kid_id);
  };

  const handleCreateReward = async (data: {
    name: string;
    description?: string;
    emoji: string;
    star_cost: number;
    is_shared: boolean;
    kid_id?: string | null;
  }) => {
    await api.createReward(data);
    await fetchAppState(selectedKidId);
  };

  const handleDeleteReward = async (rewardId: string) => {
    await api.deleteReward(rewardId);
    await fetchAppState(selectedKidId);
  };

  const handleRedeemReward = async (rewardId: string, pin: string, kidId: string) => {
    await api.redeemReward(rewardId, pin, kidId);
    await fetchAppState(selectedKidId);
  };

  const handleLogout = async () => {
    await api.logout();
    setUser(null);
    setIsAdultZoneOpen(false);
  };

  if (loading) {
    return (
      <div className="app-container" style={{ display: 'grid', placeItems: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', animation: 'spin 1.5s linear infinite' }}>⭐</div>
          <p style={{ marginTop: '14px', fontWeight: 700, color: '#64748b' }}>
            Loading quests...
          </p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="app-container">
        <LoginModal
          onLoginSuccess={async (newUser) => {
            setUser(newUser);
            await fetchAppState();
          }}
        />
      </div>
    );
  }

  const selectedKid = kids.find((k) => k.id === selectedKidId) || kids[0];

  const readyRewardsCount = rewards.filter((r) => {
    const isRelevant = r.is_shared === 1 || r.kid_id === selectedKidId;
    const currentStars = r.is_shared === 1 ? sharedStars : (selectedKid?.personal_stars_available || 0);
    return isRelevant && currentStars >= r.star_cost;
  }).length;

  return (
    <div className="app-container">
      {/* Header */}
      <Header
        user={user}
        kids={kids}
        selectedKidId={selectedKidId}
        onSelectKid={handleSelectKid}
        onOpenAdultZone={() => setIsAdultZoneOpen(true)}
        onOpenAddKid={() => setIsAddKidOpen(true)}
      />

      {/* Main Status & Streaks Card */}
      <TodayCard
        kid={selectedKid}
        quests={quests}
        todayDateStr={todayDateStr}
      />

      {/* Navigation Tabs */}
      <Navigation
        activeTab={activeTab}
        onChangeTab={setActiveTab}
        readyRewardsCount={readyRewardsCount}
      />

      {/* Active Tab View */}
      <main>
        {activeTab === 'today' && (
          <TodayView
            quests={quests}
            rewards={rewards}
            selectedKidId={selectedKidId}
            onToggleQuest={handleToggleQuest}
            onOpenAddQuest={() => setIsAddQuestOpen(true)}
            onNavigateToRewards={() => setActiveTab('rewards')}
          />
        )}

        {activeTab === 'rewards' && (
          <RewardsView
            kid={selectedKid}
            sharedStars={sharedStars}
            rewards={rewards}
            onOpenRedeemModal={(reward) => setSelectedRewardToRedeem(reward)}
          />
        )}

        {activeTab === 'manage' && (
          <ManageView
            kid={selectedKid}
            quests={quests}
            onOpenAddQuest={() => setIsAddQuestOpen(true)}
            onDeleteQuest={handleDeleteQuest}
            onOpenAdultZone={() => setIsAdultZoneOpen(true)}
          />
        )}
      </main>

      {/* Modals */}
      <QuestModal
        isOpen={isAddQuestOpen}
        kid={selectedKid}
        onClose={() => setIsAddQuestOpen(false)}
        onSubmit={handleCreateQuest}
      />

      <AddKidModal
        isOpen={isAddKidOpen}
        onClose={() => setIsAddKidOpen(false)}
        onSubmit={handleCreateKid}
      />

      <AdultZoneModal
        isOpen={isAdultZoneOpen}
        user={user}
        kids={kids}
        rewards={rewards}
        redemptions={redemptions}
        onClose={() => setIsAdultZoneOpen(false)}
        onCreateReward={handleCreateReward}
        onDeleteReward={handleDeleteReward}
        onPinUpdated={() => fetchAppState(selectedKidId)}
        onLogout={handleLogout}
      />

      <RedeemModal
        isOpen={Boolean(selectedRewardToRedeem)}
        reward={selectedRewardToRedeem}
        kid={selectedKid}
        onClose={() => setSelectedRewardToRedeem(null)}
        onRedeem={handleRedeemReward}
      />
    </div>
  );
};
